import {
    AIRisks,
    CONFORMITY_OBLIGATION_STATUS,
    PROVIDER_CONFORMITY_STATUS,
} from "src/modules/data-protection/constants";

const ANSWER_SECTION_LABELS = {
    ai_risk_assessment: "AI Risk Assessment",
    conformity_assessment: "Conformity Assessment",
};

export const formatAnswerValue = (value) => {
    if (value === null || value === undefined || value === "") {
        return "-";
    }

    if (typeof value === "boolean") {
        return value ? "Yes" : "No";
    }

    if (typeof value === "string" || typeof value === "number") {
        return String(value);
    }

    if (Array.isArray(value)) {
        if (value.length === 0) {
            return "-";
        }

        return value
            .map((item) => formatAnswerValue(item))
            .filter((item) => item && item !== "-")
            .join(", ") || "-";
    }

    if (typeof value === "object") {
        if (
            value.title !== undefined ||
            value.value !== undefined ||
            value.answer !== undefined
        ) {
            return formatAnswerValue(
                value.value !== undefined
                    ? value.value
                    : value.answer !== undefined
                      ? value.answer
                      : value.title
            );
        }

        const entries = Object.entries(value);
        if (entries.length === 0) {
            return "-";
        }

        return entries
            .map(([key, nested]) => `${key}: ${formatAnswerValue(nested)}`)
            .join("; ");
    }

    return String(value);
};

export const humanizeKey = (key) => {
    const acronyms = new Set([
        "ai",
        "eu",
        "qms",
        "gpai",
        "iii",
        "iv",
        "rms",
        "doc",
    ]);

    return String(key || "")
        .split("_")
        .filter(Boolean)
        .map((part) => {
            const lower = part.toLowerCase();
            if (acronyms.has(lower)) {
                return lower.toUpperCase();
            }

            return lower.charAt(0).toUpperCase() + lower.slice(1);
        })
        .join(" ");
};

const mapStatusLabel = (status, statusMap) => {
    const config = statusMap?.[status];
    return config?.label || status || "-";
};

const mapAiRiskLabel = (aiRisk) => {
    if (!aiRisk) {
        return null;
    }

    return AIRisks[aiRisk]?.title || humanizeKey(aiRisk);
};

/**
 * Replaces raw AI risk keys/phrases (e.g. high_risk, high-risk) in free text
 * with AIRisks titles for consistent PDF wording.
 */
const replaceAiRiskMentions = (text) => {
    if (typeof text !== "string" || !text) {
        return text;
    }

    let result = text;

    Object.keys(AIRisks)
        .sort((a, b) => b.length - a.length)
        .forEach((key) => {
            const label = mapAiRiskLabel(key);
            const hyphenated = key.replace(/_/g, "-");

            [key, hyphenated].forEach((token) => {
                const pattern = new RegExp(
                    `\\b${token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`,
                    "gi"
                );
                result = result.replace(pattern, label);
            });
        });

    return result;
};

const mapAnswerSections = (answers) => {
    if (!answers || typeof answers !== "object" || Array.isArray(answers)) {
        return [];
    }

    const sections = [];
    let flatItems = [];

    Object.entries(answers).forEach(([sectionKey, sectionValue]) => {
        // Empty questionnaire arrays (e.g. conformity_assessment: []) → skip
        if (Array.isArray(sectionValue)) {
            if (sectionValue.length === 0) {
                return;
            }

            flatItems.push({
                key: sectionKey,
                label:
                    ANSWER_SECTION_LABELS[sectionKey] ||
                    humanizeKey(sectionKey),
                value: formatAnswerValue(sectionValue),
            });
            return;
        }

        if (
            sectionValue &&
            typeof sectionValue === "object"
        ) {
            const items = Object.entries(sectionValue).map(([key, value]) => ({
                key,
                label: humanizeKey(key),
                value: formatAnswerValue(value),
            }));

            if (items.length === 0) {
                return;
            }

            sections.push({
                key: sectionKey,
                title:
                    ANSWER_SECTION_LABELS[sectionKey] ||
                    humanizeKey(sectionKey),
                items,
            });
            return;
        }

        if (
            sectionValue === null ||
            sectionValue === undefined ||
            sectionValue === ""
        ) {
            return;
        }

        flatItems.push({
            key: sectionKey,
            label: humanizeKey(sectionKey),
            value: formatAnswerValue(sectionValue),
        });
    });

    if (flatItems.length > 0) {
        sections.push({
            key: "answers",
            title: "Assessment Responses",
            items: flatItems,
        });
    }

    return sections;
};

const mapEvidenceItems = (items) => {
    if (!Array.isArray(items)) {
        return [];
    }

    return items
        .filter(
            (item) =>
                !!item && typeof item === "object" && !Array.isArray(item)
        )
        .map((item) => ({
            id: item.id || null,
            title: item.title || item.name || item.url || "-",
            category: item.category || null,
            type: item.type || item.source || "-",
        }));
};

const hasDecisionExport = (decision) => {
    if (decision === null || decision === undefined || decision === "") {
        return false;
    }

    if (Array.isArray(decision)) {
        return decision.length > 0;
    }

    if (typeof decision === "object") {
        return Object.keys(decision).length > 0;
    }

    return true;
};

const formatOpenFindings = (findings) => {
    if (!Array.isArray(findings) || findings.length === 0) {
        return null;
    }

    return findings
        .map((finding) => {
            if (typeof finding === "string") {
                return finding;
            }

            if (finding && typeof finding === "object") {
                return (
                    finding.title ||
                    finding.name ||
                    finding.label ||
                    finding.summary ||
                    null
                );
            }

            return null;
        })
        .filter(Boolean)
        .join("; ");
};

/**
 * Maps decision_export into readable rows.
 * Avoid dumping nested payload / conformity_gaps (already in obligations section).
 */
const mapDecisionRows = (decision) => {
    if (!hasDecisionExport(decision)) {
        return [];
    }

    if (typeof decision !== "object" || Array.isArray(decision)) {
        return [
            {
                key: "decision",
                label: "Decision",
                value: formatAnswerValue(decision),
            },
        ];
    }

    const payload =
        decision.payload &&
        typeof decision.payload === "object" &&
        !Array.isArray(decision.payload)
            ? decision.payload
            : {};

    const rows = [];
    const pushRow = (key, label, value) => {
        if (value === null || value === undefined || value === "") {
            return;
        }

        rows.push({
            key,
            label,
            value: formatAnswerValue(value),
        });
    };

    pushRow("queued", "Queued", decision.queued);

    const decisionValue = decision.decision ?? payload.decision;
    pushRow(
        "decision",
        "Decision",
        typeof decisionValue === "string"
            ? humanizeKey(decisionValue)
            : decisionValue
    );
    const rationale = decision.rationale ?? payload.rationale;
    pushRow(
        "rationale",
        "Rationale",
        typeof rationale === "string"
            ? replaceAiRiskMentions(rationale)
            : rationale
    );

    const openFindings =
        formatOpenFindings(decision.open_findings) ||
        formatOpenFindings(payload.open_findings);

    if (openFindings) {
        rows.push({
            key: "open_findings",
            label: "Open Findings",
            value: openFindings,
        });
    }

    const openFindingsCount =
        decision.open_findings_count ?? payload.open_findings_count;
    if (
        openFindingsCount !== undefined &&
        openFindingsCount !== null &&
        openFindingsCount !== "" &&
        !openFindings
    ) {
        pushRow(
            "open_findings_count",
            "Open Findings",
            openFindingsCount
        );
    }

    // Fallback: flat primitive fields only (skip nested dumps)
    if (rows.length === 0) {
        Object.entries(decision).forEach(([key, value]) => {
            if (key === "payload" || key === "conformity_gaps") {
                return;
            }

            if (value !== null && typeof value === "object") {
                return;
            }

            pushRow(key, humanizeKey(key), value);
        });
    }

    return rows;
};

/**
 * Maps GET /software/{id}/conformity/export package
 * (eu_ai_act_conformity_package_v1) into PDF view models.
 */
const mapConformityExportToPdf = (data = {}) => {
    const safeData =
        data && typeof data === "object" && !Array.isArray(data) ? data : {};
    const software =
        safeData.software &&
        typeof safeData.software === "object" &&
        !Array.isArray(safeData.software)
            ? safeData.software
            : {};
    const obligations = Array.isArray(safeData.obligations)
        ? safeData.obligations.filter(
              (obligation) =>
                  !!obligation &&
                  typeof obligation === "object" &&
                  !Array.isArray(obligation)
          )
        : [];
    const decision = safeData.decision_export;
    const decisionExists = hasDecisionExport(decision);

    return {
        schema: safeData.schema || null,
        exportedAt: safeData.exported_at || null,
        cover: {
            name: software.name || null,
            version: software.version || null,
            provider: software.provider || null,
            aiRisk: software.ai_risk || null,
            aiRiskLabel: mapAiRiskLabel(software.ai_risk),
            statusKey: software.provider_conformity_status || null,
            statusLabel: mapStatusLabel(
                software.provider_conformity_status,
                PROVIDER_CONFORMITY_STATUS
            ),
        },
        readiness: {
            statusKey: software.provider_conformity_status || null,
            statusLabel: mapStatusLabel(
                software.provider_conformity_status,
                PROVIDER_CONFORMITY_STATUS
            ),
            gapsCount:
                safeData.conformity_gaps_count !== undefined &&
                safeData.conformity_gaps_count !== null &&
                safeData.conformity_gaps_count !== ""
                    ? String(safeData.conformity_gaps_count)
                    : "-",
            decisionRows: decisionExists ? mapDecisionRows(decision) : [],
            decisionRaw:
                decisionExists &&
                (typeof decision !== "object" || Array.isArray(decision))
                    ? formatAnswerValue(decision)
                    : null,
            hasDecision: decisionExists,
        },
        obligations: obligations.map((obligation) => ({
            code: obligation.code || null,
            label: obligation.label || obligation.code || "-",
            statusKey: obligation.status || null,
            statusLabel: mapStatusLabel(
                obligation.status,
                CONFORMITY_OBLIGATION_STATUS
            ),
            isMet: obligation.status === "met",
            gapReason: obligation.gap_reason || null,
            source: obligation.source || null,
        })),
        answerSections: mapAnswerSections(safeData.answers),
        attachments: mapEvidenceItems(safeData.attachments),
        confluenceDocuments: mapEvidenceItems(safeData.confluence_documents),
    };
};

export default mapConformityExportToPdf;
